"use client";

import Image from "next/image";
import { EquipmentItem } from "@/types";
import { FaArrowRight } from "react-icons/fa6";

interface EquipmentItemCardProps {
  item: EquipmentItem;
  isSelected?: boolean;
  onSelectFolder?: (folder: EquipmentItem) => void;
  compact?: boolean;
}

export default function EquipmentItemCard({
  item,
  isSelected = false,
  onSelectFolder,
  compact = false,
}: EquipmentItemCardProps) {
  const isFolder = item.type === "folder";

  // Card styling based on whether it is selected in top folder bar
  const cardBgClass = isSelected
    ? "bg-[#76275D]  ring-1 ring-brand-green/40"
    : "bg-brand-card hover:bg-[#343534]";

  const content = (
    <div
      className={`group relative flex flex-col justify-between rounded-[10px] transition-all duration-300 hover:-translate-y-1 h-full ${cardBgClass} ${compact ? "p-4 sm:p-5" : "p-6"
        }`}
    >
      {/* Product Image */}
      <div
        className={`relative w-full aspect-[4/3] flex items-center justify-center overflow-hidden rounded-[8px] bg-white ${compact ? "mb-3" : "mb-6"
          }`}
      >
        <div className="relative w-full h-full transition-transform duration-500 ease-out group-hover:scale-105">
          <Image
            src={item.image || "/images/capabilities/v1.png"}
            alt={item.title}
            fill
            className="object-contain p-2"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        </div>
      </div>

      {/* Bottom Bar: Title (Left) + Arrow Button (Right) */}
      <div className="flex items-center justify-between gap-3 pt-2 min-w-0">
        <div className="relative group/tooltip min-w-0 flex-1">
          <h4
            title={item.title}
            className="font-poppins text-white/90 group-hover:text-white font-medium text-sm sm:text-base leading-snug truncate"
          >
            {item.title}
          </h4>

          {/* Styled Tooltip */}
          <div className="absolute bottom-full left-0 mb-2 hidden sm:group-hover/tooltip:block z-30 pointer-events-none transition-opacity duration-200">
            <div className="bg-brand-card text-white text-xs font-poppins px-3 py-1.5 rounded-[10px] border border-white/15 whitespace-normal max-w-[260px] leading-relaxed">
              {item.title}
            </div>
          </div>
        </div>

        <div
          className={`shrink-0 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-[10px] border transition-all duration-200 ${isSelected
            && "border-white group-hover:border-white  group-hover:bg-white text-white group-hover:text-black"
            }`}
        >
          <FaArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
        </div>
      </div>
    </div>
  );

  // If it's a folder, clicking anywhere or on the arrow selects the folder
  if (isFolder) {
    return (
      <button
        type="button"
        onClick={() => onSelectFolder && onSelectFolder(item)}
        className="w-full text-left outline-none border-none focus:outline-none focus:ring-0 focus-visible:outline-none rounded-[10px] block cursor-pointer"
        aria-label={`Open ${item.title} folder`}
      >
        {content}
      </button>
    );
  }

  // If it's a direct PDF, clicking opens Google Drive link in a new tab
  return (
    <a
      href={item.pdfUrl || "#"}
      target="_blank"
      rel="noopener noreferrer"
      className="w-full text-left outline-none border-none focus:outline-none focus:ring-0 focus-visible:outline-none rounded-[10px] block cursor-pointer"
      aria-label={`Open ${item.title} PDF in Google Drive`}
    >
      {content}
    </a>
  );
}
